import React, {useEffect, useLayoutEffect, useState} from 'react';
import {SafeAreaView, View, ScrollView} from 'react-native';
import _ from 'lodash';
import {Divider} from 'react-native-paper';

import sy from '~/styles';
import {
  PrimaryButton,
  RadioButton,
  Pressable,
  Text,
  LinearProgress,
  CloseButton,
  ReplacementIcon,
  ReplacementText,
  Link,
} from '~/components/controls';
import {PickupLocationDialog} from '~/components/shared/Dialogs';
import {useDispatch, useSelector} from '~/lib/hooks';
import {addJobSetReplacement, addJobSetPickupLocation} from '~/actions';
import {car as carApi} from '~/api/public';
import {REPLACEMENT_TYPE} from '~/types/service-codes';
import PublicContentView from '~/components/shared/PublicContentView';

import ShoppingCartIcon from '~/images/md-icons/shopping_cart/materialicons/24px.svg';
import HomeFilledIcon from '~/images/md-icons/home_filled/materialicons/24px.svg';

const Replacement = ({navigation, route, onDismiss}) => {
  const dealer_id = route?.params?.dealer_id;
  const is_change = route?.params?.is_change;

  const dispatch = useDispatch();
  const dealer = useSelector((state) => state.dealer);

  const license = useSelector((state) => state.license);
  const is_lease = useSelector((state) => state.is_lease);
  const is_fleet = useSelector((state) => state.is_fleet);
  const stored = useSelector((state) => ({
    replacement: state.replacement,
    pickup_location: state.pickup_location,
  }));

  const [loading, setLoading] = useState(true);
  const [replacement, setReplacement] = useState(
    stored.replacement ?? REPLACEMENT_TYPE.message,
  );
  const [contract, setContract] = useState(null);
  const [pickup_location, setPickupLocation] = useState(
    stored.replacement === REPLACEMENT_TYPE.transport
      ? stored.pickup_location
      : null,
  );
  const [showPickupDialog, setShowPickupDialog] = useState(false);

  useLayoutEffect(() => {
    navigation?.setOptions({
      headerShown: true,
      headerStatusBarHeight: 0,
      headerTitleAlign: 'center',
      headerTitle: () => (
        <LinearProgress style={{width: 126}} step={6} total={7} />
      ),
    });
  }, [navigation]);

  useEffect(() => {
    const fetch = async () => {
      const {result: contract} = await carApi.contract(dealer_id, license);
      if (is_lease) {
        setContract(contract.rob);
      } else if (is_fleet) {
        setContract(contract.fleet);
      }

      setLoading(false);
    };

    fetch();
  }, [dealer, license]);

  useEffect(() => {
    if (replacement !== REPLACEMENT_TYPE.transport) {
      setPickupLocation(null);
    }
  }, [replacement]);

  const onNext = async () => {
    dispatch(addJobSetReplacement(replacement));
    if (pickup_location) {
      dispatch(addJobSetPickupLocation(pickup_location));
    }

    if (is_change && navigation.canGoBack()) {
      navigation.goBack();
    } else if (onDismiss) {
      onDismiss();
    } else {
      navigation.navigate('Confirm', {dealer_id});
    }
  };

  const content = (
    <>
      {!onDismiss ? (
        <>
          <Divider />
          <View style={[sy['p-4']]}>
            <Text style={sy.xxLarge}>Vervangend vervoer?</Text>
            <Text style={[sy.large, sy['mt-4']]}>
              Wachten tijdens het werk op locatie of maak een keuze voor
              vervangend vervoer.
            </Text>
          </View>
          <Divider />
        </>
      ) : (
        <View style={sy.modalHeaderView}>
          <CloseButton
            style={{position: 'absolute', top: 8, left: 8}}
            onPress={onDismiss}
          />
          <Text style={sy.medium}>Vervangend vervoer</Text>
        </View>
      )}
      <View style={sy.contentView}>
        {!loading && (
          <View>
            {Object.values(REPLACEMENT_TYPE)
              .filter((type) => ![REPLACEMENT_TYPE.none].includes(type))
              .map((type, index, arr) => {
                let allowed = true;
                if (type === REPLACEMENT_TYPE.car) {
                  allowed =
                    contract?.permissions?.replacement_vehicle?.allowed ?? true;
                }

                return (
                  <Pressable
                    key={type}
                    disabled={!allowed}
                    style={[sy['flex-row'], sy['flex-1']]}
                    onPress={() => setReplacement(type)}>
                    <View style={[sy['p-4']]}>
                      <RadioButton
                        disabled={!allowed}
                        checked={replacement === type}
                      />
                    </View>
                    <View style={[sy['flex-1'], sy['pl-4']]}>
                      <View
                        style={[
                          sy['flex-row'],
                          sy['flex-1'],
                          sy['items-center'],
                          sy['justify-between'],
                          sy['py-4'],
                          sy['pr-4'],
                        ]}>
                        <View>
                          <ReplacementText type={type} />
                          {!allowed && (
                            <Text
                              style={[sy.smallRegular, sy['text-lightgray']]}>
                              Niet in contract
                            </Text>
                          )}
                        </View>
                        <ReplacementIcon type={type} fill="#4A4A49" />
                      </View>
                      {index < arr.length - 1 && (
                        <Divider style={[{backgroundColor: '#f2f2f2'}]} />
                      )}
                    </View>
                  </Pressable>
                );
              })}
            {replacement === REPLACEMENT_TYPE.transport && (
              <View style={[sy['flex-row'], sy['flex-1'], sy['items-center']]}>
                <View style={[sy['p-4']]}>
                  <HomeFilledIcon fill="#4A4A49" width={24} height={24} />
                </View>
                <View style={[sy['flex-1'], sy['px-4']]}>
                  <Link onPress={() => setShowPickupDialog(true)}>
                    {pickup_location ? (
                      <>{pickup_location.address}</>
                    ) : (
                      <>Adres opgeven</>
                    )}
                  </Link>
                </View>
              </View>
            )}
          </View>
        )}
      </View>
    </>
  );

  const footer = (
    <View style={sy.footerView}>
      <PrimaryButton
        style={{minWidth: 152}}
        onPress={onNext}
        icon={
          !onDismiss &&
          (() => <ShoppingCartIcon fill="#ffffff" width={18} height={18} />)
        }>
        {onDismiss ? 'Opslaan' : 'Controleren'}
      </PrimaryButton>
    </View>
  );

  return (
    <>
      <SafeAreaView style={sy.mainView}>
        {onDismiss ? (
          <>
            <ScrollView>{content}</ScrollView>
            <View style={{borderTopWidth: 1, borderTopColor: '#dcdcdc'}}>
              {footer}
            </View>
          </>
        ) : (
          <PublicContentView>
            {{
              content,
              footer,
            }}
          </PublicContentView>
        )}
      </SafeAreaView>
      <PickupLocationDialog
        visible={showPickupDialog}
        onDismiss={() => setShowPickupDialog(false)}
        onOK={(location) => {
          setPickupLocation(location);
          setShowPickupDialog(false);
        }}
      />
    </>
  );
};

export default Replacement;
